import styled from '@emotion/styled';
import { Layout as AntdLayout, Menu } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import {
  AngleLeft,
  AngleRight,
  Building,
  Cars,
  ListChecks,
  TableLayout,
  User,
  Users,
} from '@/assets/icons';
import Header from '@/components/Header';
import { useApplication } from '@/context/application.provider';
import { colors, fonts } from '@/styles/theme';

const { Content, Sider } = AntdLayout;

const LoginLayout = styled(AntdLayout)`
  font-family: ${fonts.InterRegular} !important;
  height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;

  .ant-layout-content {
    display: flex;
    flex-direction: row;
    flex: 1;
    margin-top: 60px;
    height: calc(100vh - 60px);
    overflow-y: auto;

    #left-side {
      height: calc(100vh - 95px);
    }

    @media (max-width: 768px) {
      #left-side {
        display: none;
      }

      #title {
        font-size: 28px;
      }

      #subtitle {
        font-size: 14px;
        text-align: center;
      }
    }

    @media (min-width: 640px) {
      margin-top: 80px;
      height: calc(100vh - 80px);
    }

    @media (min-width: 1024px) {
      margin-top: 95px;
      height: calc(100vh - 95px);
    }
  }
`;

const DashboardLayout = styled(AntdLayout, {
  shouldForwardProp: (prop) => prop !== '$collapsed' && prop !== '$showSider',
})<{ $collapsed?: boolean; $showSider?: boolean }>`
  font-family: ${fonts.InterRegular} !important;
  height: 100vh;
  background: #ffffff;
  display: flex;
  flex-direction: column;

  .ant-layout-sider {
    background: #ffffff !important;
    border-right: 1px solid #e5e7eb;
    position: fixed;
    top: 60px;
    left: 0;
    bottom: 0;
    z-index: 999;
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
    transition: transform 0.3s ease;

    @media (min-width: 640px) {
      top: 80px;
    }

    @media (min-width: 1024px) {
      top: 95px;
    }
  }

  .ant-layout {
    flex: 1;
    background: #ffffff;

    .ant-layout-content {
      background: #ffffff;
      margin-top: 60px;
      height: calc(100vh - 60px);
      margin-left: 0;
      padding: 0.75rem;
      overflow-y: auto;
      overflow-x: hidden;
      transition: margin-left 0.3s ease;

      @media (min-width: 640px) {
        margin-top: 80px;
        height: calc(100vh - 80px);
        padding: 1rem;
      }

      @media (min-width: 1024px) {
        margin-top: 95px;
        height: calc(100vh - 95px);
        margin-left: ${(props) => (!props.$showSider ? '0' : props.$collapsed ? '68px' : '280px')};
        padding: 1.5rem;
      }
    }
  }

  .ant-menu {
    all: unset;
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 16px !important;

    @media (min-width: 640px) {
      padding: 20px !important;
    }

    @media (min-width: 1024px) {
      padding: 24px !important;
    }

    .ant-menu-item {
      all: unset;
      display: inline-flex !important;
      align-items: start !important;
      display: block;
      width: 100%;
      box-sizing: border-box;
      cursor: pointer;
      padding: 0px !important;
      margin-bottom: 16px !important;
      background: white !important;

      @media (min-width: 1024px) {
        margin-bottom: 20px !important;
      }

      .ant-menu-title-content {
        font-size: 14px !important;

        @media (min-width: 640px) {
          font-size: 15px !important;
        }

        @media (min-width: 1024px) {
          font-size: 16px !important;
        }
      }

      &::before,
      &::after {
        display: none !important;
      }

      &:hover,
      &:focus,
      &:active,
      &.ant-menu-item-selected {
        all: unset;
        display: block;
        width: 100%;
        box-sizing: border-box;
        cursor: pointer;

        svg > path {
          fill: ${colors.primary} !important;
        }

        .ant-menu-title-content {
          color: ${colors.primary} !important;
        }
      }
    }
  }

  .ant-menu-inline-collapsed {
    .ant-menu-item {
      all: unset;
      display: block;
      width: 100%;
      box-sizing: border-box;
      cursor: pointer;

      span {
        display: none;
      }

      &::before,
      &::after {
        display: none !important;
      }
    }
  }

  .ant-menu-inline-collapsed-tooltip {
    min-width: auto !important;

    .ant-tooltip-content {
      padding: 8px !important;
    }
  }

  .collapse-button {
    position: absolute;
    top: 20px;
    right: -10px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    z-index: 1001;
    transition: all 0.2s ease;
  }

  .mobile-overlay {
    display: none;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 998;

    @media (max-width: 1023px) {
      display: ${(props) => (props.$collapsed ? 'none' : 'block')};
    }
  }

  @media (max-width: 1023px) {
    .ant-layout-sider {
      transform: translateX(${(props) => (props.$collapsed ? '-100%' : '0')});
      width: 280px !important;
      min-width: 280px !important;
      max-width: 280px !important;
      flex: 0 0 280px !important;
    }

    .collapse-button {
      display: none !important;
    }
  }

  @media (max-width: 1023px) {
    .ant-menu-inline-collapsed-tooltip,
    body > .ant-tooltip[role='tooltip'] {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
  }

  @media (max-width: 767px) {
    .ant-layout-sider {
      width: 260px !important;
      min-width: 260px !important;
      max-width: 260px !important;
      flex: 0 0 260px !important;
    }
  }
`;

interface DashboardLayoutProps {
  children: React.ReactNode;
  className?: string;
  contentClassName?: string;
  headerContent?: React.ReactNode;
}

const additionalRoutesWithSider = ['/my-profile', '/claim-role'];

const Layout = ({ children, className, contentClassName, headerContent }: DashboardLayoutProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const { auth } = useApplication();
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  const handleCollapse = (broken: boolean) => {
    if (broken) {
      setIsMobile(true);
      setCollapsed(true);
    } else {
      setIsMobile(false);
      const collapsedFromSession = sessionStorage.getItem('collapsed');
      setCollapsed(collapsedFromSession === 'true');
    }
  };

  const toggleSidebar = () => {
    if (isMobile) {
      setCollapsed(!collapsed);
    } else {
      const newCollapsed = !collapsed;
      sessionStorage.setItem('collapsed', newCollapsed.toString());
      setCollapsed(newCollapsed);
    }
  };

  const menuItems = useMemo(
    () => [
      {
        icon: <TableLayout className="h-5 w-5" />,
        key: '/home',
        label: t('general.navigation.home'),
      },
      {
        icon: <User className="h-5 w-5" />,
        key: '/users',
        label: t('general.navigation.users'),
      },
      {
        icon: <Building className="h-5 w-5" />,
        key: '/work-centers',
        label: t('general.navigation.workCenters'),
      },
      {
        icon: <Users className="h-5 w-5" />,
        key: '/workers',
        label: t('general.navigation.workers'),
      },
      {
        icon: <Cars className="h-5 w-5" />,
        key: '/vehicles',
        label: t('general.navigation.vehicles'),
      },
      {
        icon: <ListChecks className="h-5 w-5" />,
        key: '/assigned-accreditation-processes',
        label: t('general.navigation.accreditationProcesses'),
      },
    ],
    [t]
  );

  const routesWithSider = useMemo(
    () => [...menuItems.map((item) => item.key), ...additionalRoutesWithSider],
    [menuItems]
  );

  const showSider = useMemo(
    () => routesWithSider.includes(location.pathname),
    [location.pathname, routesWithSider]
  );

  useEffect(() => {
    const collapsedFromSession = sessionStorage.getItem('collapsed');
    const checkMobile = () => window.innerWidth < 1024;

    setIsMobile(checkMobile());

    if (!checkMobile() && collapsedFromSession) {
      setCollapsed(collapsedFromSession === 'true');
    } else if (checkMobile()) {
      setCollapsed(true);
    }

    const handleResize = () => {
      const mobile = checkMobile();
      setIsMobile(mobile);
      if (mobile) {
        setCollapsed(true);
      } else {
        const collapsedFromSession = sessionStorage.getItem('collapsed');
        setCollapsed(collapsedFromSession === 'true');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!auth.isLogged) {
    return (
      <LoginLayout className={className}>
        <Header headerContent={headerContent} />
        <Content className={contentClassName}>{children}</Content>
      </LoginLayout>
    );
  }

  return (
    <DashboardLayout $collapsed={collapsed} $showSider={showSider} className={className}>
      <Header
        headerContent={headerContent}
        onMenuToggle={isMobile ? toggleSidebar : undefined}
        showMenuButton={isMobile}
      />
      <AntdLayout>
        {!collapsed && isMobile && (
          <div className="mobile-overlay" onClick={() => setCollapsed(true)} />
        )}
        {showSider && (
          <Sider
            breakpoint="lg"
            collapsed={collapsed}
            collapsedWidth={68}
            collapsible
            onBreakpoint={handleCollapse}
            trigger={null}
            width={280}
          >
            <div className="collapse-button" onClick={toggleSidebar}>
              {collapsed ? (
                <AngleRight className="h-3.25 w-1.75" />
              ) : (
                <AngleLeft className="h-3.25 w-1.75" />
              )}
            </div>
            <Menu
              items={menuItems}
              mode="inline"
              onClick={({ key }) => {
                navigate(key);
                if (isMobile) {
                  setCollapsed(true);
                }
              }}
              selectedKeys={[location.pathname]}
            />
          </Sider>
        )}
        <AntdLayout>
          <Content className={contentClassName}>{children}</Content>
        </AntdLayout>
      </AntdLayout>
    </DashboardLayout>
  );
};

export default Layout;
